import { defineStore } from "pinia";
import { PrintingOrder } from "./entity";
import { PrintingOrderList } from "./list.gen";
import type {
  PrintingOrderStore,
  PrintingOrderStoreState,
} from "./store.gen.d";
import { analytics, auth } from "@/firebase";
import { type JSONData } from "../../base_classes/dto.d";
import { Pagination } from "../../base_classes/pagination";
import { Query } from "../../base_classes/query";
import { paginationAndQueriesToCacheKey } from "../../base_classes/dto_list";

/// ///////////////////////////////////////////////////////////////////////////////////////////////////
/// WARNING! THIS FILE IS AUTOMATICALLY GENERATED, ANY CHANGES WILL BE LOST ON NEXT GENERATION
/// IF YOU WANT TO EDIT THIS FILE, REMOVE THE .gen. FROM THE FILENAME. THE FILE WILL THEN BE IGNORED.
/// ///////////////////////////////////////////////////////////////////////////////////////////////////

const printingOrderList = new PrintingOrderList();
export const usePrintingOrderStore = defineStore("printingOrderStore", {
  state: (): PrintingOrderStoreState => ({
    activeID: null,
    cache: {},
    list: [] as PrintingOrder[],
    retrievalTimestamp: 0,
    updateKey: 0,
    storeUpdateTimeout: null,
  }),
  actions: {
    initializeStore(): void {
      this.convertList();
    },
    emitFormUpdateEvent(id: string): void {
      window.dispatchEvent(
        new CustomEvent("form-update-PrintingOrder", { detail: { id } }),
      );
    },
    emitStoreUpdateEvent(): void {
      // Clear existing timeout
      if (this.storeUpdateTimeout !== null) {
        clearTimeout(this.storeUpdateTimeout);
      }

      // Set new timeout
      this.storeUpdateTimeout = setTimeout(() => {
        window.dispatchEvent(new CustomEvent("store.update.PrintingOrder"));
        this.storeUpdateTimeout = null;
      }, 1500);
    },
    async activate(printingOrder: PrintingOrder): Promise<void> {
      // Set active
      await printingOrder
        .activate(auth.firebaseUser?.uid)
        .then(() => {
          this.activeID = printingOrder.id;
        })
        .catch((error) => {
          console.error(error);
          this.activeID = null;
        });
    },
    add(printingOrder: PrintingOrder): void {
      const id = printingOrder.id;
      if (typeof id === "string" && !this.contains(id)) {
        this.list.push(printingOrder);

        // Set active
        if (this.active === null) {
          this.activeID = printingOrder.id;
        }
      } else {
        this.reinsert(printingOrder);
      }
      this.updateKey += 1;
    },
    convertList(): void {
      this.list = (this.list as PrintingOrder[]).map((x: any) => {
        const printingOrder = new PrintingOrder({});
        printingOrder.set(x as JSONData);
        return printingOrder;
      });
    },
    async delete(printingOrder: PrintingOrder): Promise<void> {
      await printingOrder
        .delete()
        .then(() => {
          this.remove(printingOrder);
        })
        .finally(() => {
          this.emitStoreUpdateEvent();
        });
    },
    async deleteById(id: string): Promise<void> {
      const printingOrder = (this.list as PrintingOrder[]).find(
        (x: PrintingOrder) => x.id === id,
      );
      if (printingOrder !== undefined) {
        await this.delete(printingOrder);
      } else {
        throw new Error("Could not find PrintingOrder with id: " + id);
      }
    },
    contains(id: string): boolean {
      return (this.list as PrintingOrder[]).some(
        (x: PrintingOrder) => x.id === id,
      );
    },
    get(id: string): PrintingOrder | null {
      return (
        (this.list as PrintingOrder[]).find(
          (x: PrintingOrder) => x.id === id,
        ) || null
      );
    },
    reinsert(printingOrder: PrintingOrder): void {
      const index = (this.list as PrintingOrder[]).findIndex(
        (x: PrintingOrder) => x.id === printingOrder.id,
      );
      if (index !== -1) {
        this.list = [
          ...this.list.slice(0, index),
          printingOrder,
          ...this.list.slice(index + 1),
        ];
      } else {
        this.add(printingOrder);
      }
      this.updateKey += 1;
    },
    remove({ id }: PrintingOrder): void {
      const index = (this.list as PrintingOrder[]).findIndex(
        (x: PrintingOrder) => x.id === id,
      );
      if (index !== -1) {
        this.list = [
          ...this.list.slice(0, index),
          ...this.list.slice(index + 1),
        ];
      }
      this.updateKey += 1;
    },
    async retrieve(id: string, force = false): Promise<PrintingOrder> {
      // Check if a refresh is required
      if (!force && !this.refreshRequired) {
        const printingOrder = this.get(id);
        if (printingOrder !== null) {
          return printingOrder;
        }
      }

      // Retrieve printingOrder
      const printingOrder = new PrintingOrder({});

      // Get printingOrder
      await printingOrder.get([id]).then(async (entity) => {
        this.reinsert(entity);

        // Activate printingOrder
        await this.activate(entity);
      });

      return printingOrder;
    },
    async retrieveAll(force = false): Promise<{
      active: string | null;
      items: PrintingOrder[];
    }> {
      // Check if a refresh is required
      if (!force && !this.refreshRequired) {
        return {
          active: this.activeID,
          items: this.list as PrintingOrder[],
        };
      }

      // Clear list
      while (this.list.length > 0) {
        this.list.pop();
      }

      // Retrieve list
      await printingOrderList.getAll().then(async (list) => {
        list.forEach((printingOrder) => {
          this.reinsert(printingOrder);
        });

        // Activate printingOrder
        if (list.length > 0) {
          if (printingOrderList.active !== null) {
            this.activeID = printingOrderList.active;
          } else {
            await this.activate(list[0]);
          }
        }
      });

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      // Set user property
      if (this.activeID !== null) {
        analytics.setUserProperty("printing_order_id", this.activeID);
      }

      return {
        active: this.activeID,
        items: this.list as PrintingOrder[],
      };
    },
    async retrievePaginated(
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: PrintingOrder[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey = pagination.toCacheKey();

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          pagination: cached.pagination.deepCopy(),
          items: cached.ids.map((id: string) => this.get(id) as PrintingOrder),
        };
      }

      // Retrieve list
      const items = await printingOrderList
        .getPaginated([], pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((printingOrder) => {
            this.reinsert(printingOrder);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: PrintingOrder) => x.id),
        pagination: newPagination,
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        pagination: newPagination,
        items,
      };
    },
    async query(
      queries: Query[],
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: PrintingOrder[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey = paginationAndQueriesToCacheKey(pagination, queries);

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          pagination: cached.pagination.deepCopy(),
          items: cached.ids.map((id: string) => this.get(id) as PrintingOrder),
        };
      }

      // Retrieve list
      const items = await printingOrderList
        .query(queries, pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((printingOrder) => {
            this.reinsert(printingOrder);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: PrintingOrder) => x.id),
        pagination: newPagination.deepCopy(),
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        pagination: newPagination,
        items: items,
      };
    },
    async search(
      query: string,
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: PrintingOrder[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey =
        query.replaceAll(" ", "_") + "-" + pagination.toCacheKey();

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          items: cached.ids.map((id: string) => this.get(id) as PrintingOrder),
          pagination: cached.pagination.deepCopy(),
        };
      }

      // Retrieve list
      const items = await printingOrderList
        .search(query, pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((printingOrder) => {
            this.reinsert(printingOrder);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: PrintingOrder) => x.id),
        pagination: newPagination.deepCopy(),
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        items: items,
        pagination: newPagination,
      };
    },
    async update(printingOrder: PrintingOrder): Promise<string> {
      // Update printingOrder
      await printingOrder.update();

      // Reinsert printingOrder
      this.reinsert(printingOrder);

      // Activate printingOrder
      if (this.active === null) {
        await this.activate(printingOrder);
      }

      this.emitStoreUpdateEvent();

      return printingOrder.id;
    },
  },
  getters: {
    active(): PrintingOrder | null {
      if (this.activeID === null) {
        return null;
      }

      const printingOrder = (this.list as PrintingOrder[]).find(
        (x: PrintingOrder) => x.id === this.activeID,
      );
      if (printingOrder === undefined) {
        return null;
      }

      return printingOrder;
    },
    refreshRequired(): boolean {
      return (
        this.retrievalTimestamp === 0 ||
        this.retrievalTimestamp < Date.now() - 30 * 60000 ||
        this.list.length === 0
      );
    },
  },
});

// Export a function to create a new instance of the store
export function createPrintingOrderStore(): PrintingOrderStore {
  const store = usePrintingOrderStore();
  // Run the initializeStore action when the store is created
  store.initializeStore();
  return store as unknown as PrintingOrderStore; // TODO: Fix this
}
